import React from 'react';
import { observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import { colors } from 'shared/themes';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MemoizedPlaidEmbeddedSearch } from 'src/payments/components/plaid-embedded-search';
import { StyledButton as AccordionStyledButton } from 'shared/components/checkout-accordion';
import {
  BASE_MAX_WIDTH,
  PLAID_OFFSET,
  PlaidLinkCustomizationNames,
  RELINK_ALERT_MESSAGE,
} from 'src/payments/constants';
import { EnrolledBankCard } from 'src/payments/components/enrolled-bank-card';
import { PaymentBanner } from 'src/checkout/components/sections/payment/shared/banner';
import { PaymentHeader } from 'src/checkout/components/sections/payment/shared/payment-summary/header';
import { usePayByBankDetails } from './use-pay-by-bank-details';
import { PayByBankSignup } from '../pay-by-bank-signup';

const { EMBEDDED_SEARCH_ONLINE_POPUP, EMBEDDED_SEARCH_ONLINE } = PlaidLinkCustomizationNames;
const EMBEDDED_MAX_WIDTH = PLAID_OFFSET * 2 + BASE_MAX_WIDTH;
export const SAVE_TEST_ID = 'checkout-expansion-save-payment-delivery';

export const PayByBankDetails = observer(
  (): JSX.Element => {
    const {
      bankDetails,
      bankNeedsRelinking,
      showBankInformation,
      loggedOutPBBSignupEnabled,
      loggedInPBBSignupEnabled: showEmbeddedSearch,
      hasOtherOrderTypesWithPBBEnabled,
      unenrolledAlertMessage,
      insideTabContainer,
      showSaveButton,
      onlinePaymentOption,
      hasOnlineAndOfflinePaymentOptions,
      whiteLabelConfig,
      handleEmbeddedDutchiePayLink,
      onEditBank,
      onRelinkBank,
      saveOnlinePaymentMethod,
      openConnectingBankModal,
      closeConnectingBankModal,
    } = usePayByBankDetails();

    const flags = useFlags();

    const showSignup =
      !showBankInformation && !showEmbeddedSearch && (loggedOutPBBSignupEnabled || hasOtherOrderTypesWithPBBEnabled);

    const showEnrollment = showEmbeddedSearch || showSignup;

    const embeddedSearchPopupEnabled = flags['fintech.platform.plaid-embedded-search-popup'] ?? false;

    const { value: paymentValue, sublabel: paymentFee = '', label: paymentLabel = '' } = onlinePaymentOption ?? {};

    return (
      <>
        {/* User is logged in and signed up for Pay By Bank */}
        {showBankInformation && (
          <EnrolledBankContainer hasError={bankNeedsRelinking} requiresBorder={!hasOnlineAndOfflinePaymentOptions}>
            {!hasOnlineAndOfflinePaymentOptions && (
              <PaymentHeader
                paymentValue={paymentValue}
                paymentLabel={paymentLabel}
                paymentFee={paymentFee}
                whiteLabelConfig={whiteLabelConfig}
              />
            )}
            {bankNeedsRelinking && (
              <PaymentBanner title='Action needed:' description={RELINK_ALERT_MESSAGE} severity='errorV2' />
            )}
            {bankDetails && (
              <EnrolledBankCard
                bankDetails={bankDetails}
                bankNeedsRelinking={bankNeedsRelinking}
                onEditBank={onEditBank}
                onRelinkBank={onRelinkBank}
              />
            )}
            {showSaveButton && (
              <AccordionStyledButton
                width='73px'
                height='33px'
                onClick={saveOnlinePaymentMethod}
                data-cy={SAVE_TEST_ID}
                data-test={SAVE_TEST_ID}
                data-testid={SAVE_TEST_ID}
              >
                Save
              </AccordionStyledButton>
            )}
          </EnrolledBankContainer>
        )}
        {/* User is logged in, not enrolling through the modal flow but not enrolled in Dutchie Pay */}
        {showEnrollment && (
          <EnrollmentContainer
            insideTabContainer={insideTabContainer}
            showSignup={showSignup}
            showEmbeddedSearch={showEmbeddedSearch}
            hasError={!!unenrolledAlertMessage}
          >
            {showEmbeddedSearch && (
              <>
                <EmbeddedSearchWrapper
                  hasError={!!unenrolledAlertMessage}
                  data-testid='plaid-embedded-search-container'
                >
                  <MemoizedPlaidEmbeddedSearch
                    handleEmbeddedDutchiePayLink={handleEmbeddedDutchiePayLink}
                    handleOnPlaidConnectBank={openConnectingBankModal}
                    handleOnError={closeConnectingBankModal}
                    linkCustomizationName={
                      embeddedSearchPopupEnabled ? EMBEDDED_SEARCH_ONLINE_POPUP : EMBEDDED_SEARCH_ONLINE
                    }
                    width='100%'
                  />
                </EmbeddedSearchWrapper>
              </>
            )}
            {/* User is not enrolled in Dutchie Pay */}
            {showSignup && <PayByBankSignup />}
          </EnrollmentContainer>
        )}
      </>
    );
  }
);

const getEnrollmentBorder = (showEmbeddedSearch: boolean, hasError: boolean): string => {
  if (showEmbeddedSearch) {
    return 'none';
  }
  if (hasError) {
    return '1px solid #D34A4A';
  }
  return `1px solid ${colors.grey[85]}`;
};

const EnrollmentContainer = styled.div<{
  insideTabContainer: boolean;
  showSignup: boolean;
  showEmbeddedSearch: boolean;
  hasError: boolean;
}>`
  width: 100%;
  ${({ insideTabContainer, theme: { customized, spaces }, showSignup, showEmbeddedSearch, hasError }) =>
    !insideTabContainer &&
    css`
      max-width: ${showEmbeddedSearch ? EMBEDDED_MAX_WIDTH : BASE_MAX_WIDTH}px;
      margin-left: ${showEmbeddedSearch ? `-${PLAID_OFFSET}px` : '0'};
      border: ${getEnrollmentBorder(showEmbeddedSearch, hasError)};
      border-radius: ${customized.radius.md};
      padding: ${showSignup || showEmbeddedSearch ? '0' : spaces[4]};
      background-color: ${showEmbeddedSearch ? 'transparent' : colors.white};
    `}
`;

const EmbeddedSearchWrapper = styled.div<{ hasError?: boolean }>`
  width: 100%;
  ${({ hasError }) =>
    hasError &&
    css`
      outline: 1px solid #dc2626;
      outline-offset: -4px;
      border-radius: 16px;
    `}
`;

const EnrolledBankContainer = styled.div<{ hasError?: boolean; requiresBorder?: boolean }>`
  ${({ theme: { spaces, customized }, hasError, requiresBorder }) => css`
    display: flex;
    padding: ${spaces[4]};
    flex-direction: column;
    justify-content: start;
    align-items: flex-start;
    align-self: stretch;
    gap: ${spaces[4]};
    border: ${requiresBorder ? `1px solid ${hasError ? '#D34A4A' : '#d8dee3'}` : 'none'};
    border-radius: ${customized.radius.md};
    background: ${colors.white};
  `}
`;
